import { useMemo } from 'react'
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import QueryState from './components/query_state'
import { CalendarBlank, HardHat } from '@phosphor-icons/react'
import Dashboard from './pages/dashboard'
import Login from './pages/login'
import Cycle from './pages/cycle'
import Issues from './pages/issues'
import Metadata from './pages/metadata'
import Account from './pages/account'
import Categories from './pages/metadata/categories'
import Category from './pages/category'
import Images from './pages/images'
import Lists from './pages/lists'
import List from './pages/list'
import Ads from './pages/ads'
import GroupsPools from './pages/ads/groups_pools'
import AirlineCycleWrapper from './components/airline_cycle_wrapper'
import AdministrationModal from './components/administration_modal'
import LandingPage from './pages/landing_page'
import Files from './pages/files'
import Publishing from './pages/publishing'

const isAdministrationPath = (pathname) => pathname.startsWith('/settings') && !pathname.startsWith('/settings/account')

const administrationBackground = () => {
  const stored = localStorage.getItem('backToMetadata')
  if (stored && stored !== '/' && !stored.startsWith('/settings')) {
    return { pathname: stored, search: '', hash: '', state: null, key: 'administration-background' }
  }
  return { pathname: '/administration-backdrop', search: '', hash: '', state: null, key: 'administration-background' }
}

const MessagePage = ({ message, icon }) => {
  return (
    <div className='flex h-screen items-center justify-center text-xl'>
      <div className='flex-col space-y-4'>
        <div className='flex justify-center'>{icon}</div>
        <div>{message}</div>
      </div>
    </div>
  )
}
const ComingSoon = () => {
  return <MessagePage message='Coming Soon.' icon={<HardHat size={64} />} />
}

const AdsIndexRedirect = () => {
  const { airlineID, cycleID } = useParams()
  return <Navigate to={`/airlines/${airlineID}/cycles/${cycleID}/ads/clips`} replace />
}

const LegacyAdsAssetsRedirect = () => {
  const location = useLocation()
  return <Navigate to={`${location.pathname.replace('/ads/assets', '/ads/clips')}${location.search}`} replace />
}

const LegacyGroupsRedirect = () => {
  const { airlineID, cycleID } = useParams()
  const [searchParams] = useSearchParams()
  const segment = searchParams.get('type') === 'warning-slates' ? 'slate-sets' : 'ad-sets'
  return <Navigate to={`/airlines/${airlineID}/cycles/${cycleID}/ads/${segment}`} replace />
}

const LegacyAdPoolsRedirect = () => {
  const location = useLocation()
  return <Navigate to={`${location.pathname.replace('/ads/ad-pools', '/ads/ad-sets')}${location.search}`} replace />
}

const LegacySlateGroupsRedirect = () => {
  const location = useLocation()
  return (
    <Navigate to={`${location.pathname.replace('/ads/slate-groups', '/ads/slate-sets')}${location.search}`} replace />
  )
}

const LegacyGroupsPoolsCategoryRedirect = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const segment = searchParams.get('type') === 'warning-slates' ? 'slate-sets' : 'ad-sets'
  return (
    <Navigate to={`${location.pathname.replace('/ads/groups-pools', `/ads/${segment}`)}${location.search}`} replace />
  )
}

const LegacyPublishingRedirect = () => {
  const { airlineID, cycleID } = useParams()
  return <Navigate to={`/airlines/${airlineID}/cycles/${cycleID}/submission`} replace />
}

export const RoutesList = ({ user, isUserLoading }) => {
  const location = useLocation()
  const showAdministration = !!user?.has_unrestricted_edit_role && isAdministrationPath(location.pathname)
  const background = useMemo(() => (showAdministration ? administrationBackground() : undefined), [showAdministration])

  // If we are still checking the auth user status, return a pending spinner.
  if (isUserLoading) {
    return <QueryState isLoading loadingClassName='h-screen' />
  }

  if (!user) {
    return (
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route path='*' element={<Navigate to='/login' replace={true} />} />
      </Routes>
    )
  }

  return (
    <>
      <Routes location={background}>
        <Route element={<AirlineCycleWrapper />}>
          <Route path='/' element={<LandingPage />} />
          <Route
            path='/airlines/:airlineID'
            element={<MessagePage message='Choose a cycle from the sidebar.' icon={<CalendarBlank size={64} />} />}
          />
          <Route path='/airlines/:airlineID/cycles/:cycleID/*'>
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='issues' element={<Issues />} />
            <Route path='submission' element={<Publishing />} />
            <Route path='publishing' element={<LegacyPublishingRedirect />} />
            <Route path='details' element={<Cycle />} />
            <Route path='sections' element={<Categories categoryType='categories' />} />
            <Route path='sections/:categoryID/*' element={<Category />} />
            <Route path='categories' element={<Categories categoryType='assets' />} />
            <Route path='categories/:categoryID/*' element={<Category />} />
            <Route path='images/*' element={<Images />} />
            <Route path='lists/' element={<Lists />} />
            <Route path='lists/:listID/*' element={<List />} />
            <Route path='ads' element={<AdsIndexRedirect />} />
            <Route path='ads/clips' element={<Ads />} />
            <Route path='ads/clips/:assetID/*' element={<Ads />} />
            <Route path='ads/assets' element={<LegacyAdsAssetsRedirect />} />
            <Route path='ads/assets/:assetID/*' element={<LegacyAdsAssetsRedirect />} />
            <Route path='ads/ad-sets' element={<GroupsPools categoryType='ad-pools' />} />
            <Route path='ads/ad-sets/:categoryID/*' element={<Category />} />
            <Route path='ads/slate-sets' element={<GroupsPools categoryType='warning-slates' />} />
            <Route path='ads/slate-sets/:categoryID/*' element={<Category />} />
            <Route path='ads/ad-pools' element={<LegacyAdPoolsRedirect />} />
            <Route path='ads/ad-pools/:categoryID/*' element={<LegacyAdPoolsRedirect />} />
            <Route path='ads/slate-groups' element={<LegacySlateGroupsRedirect />} />
            <Route path='ads/slate-groups/:categoryID/*' element={<LegacySlateGroupsRedirect />} />
            <Route path='ads/groups-pools' element={<LegacyGroupsRedirect />} />
            <Route path='ads/groups-pools/:categoryID/*' element={<LegacyGroupsPoolsCategoryRedirect />} />
            <Route path='metadata/*' element={<Metadata />} />
            <Route path='files' element={<Files />} />
            <Route path='*' element={<Navigate to='dashboard' replace={true} />} />
          </Route>
          <Route path='/settings/account/*' element={<Account />} />
          <Route path='/administration-backdrop' element={<></>} />
          <Route path='/coming-soon' element={<ComingSoon />} />
          {/* <Route path="*" element={<Navigate to="/airlines" replace={true} />} /> */}
        </Route>
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
      {showAdministration ? <AdministrationModal /> : null}
    </>
  )
}
